import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import type {
  ActivityAvailabilityResponse,
  AvailableTransportIngredientResponseWithStaticInfo,
  DayTicketRate,
  DestinationStaticInfo,
  DetailsStaticInfo,
  Dictionary,
  DynamicDetailedDestinationProduct,
  HotelBookableAndLockableOption,
  IngredientId,
  OverviewDestinationStaticInfo,
  StaticDetailedActivityProduct,
  StaticDetailedDestinationProduct,
} from '@libs/trip-planner-interfaces';
import { ProductSearchType } from '@libs/trip-planner-interfaces';
import { getHotelKey, isAccommodation, readonlyRef } from '@libs/utils';

import { useItineraryStore } from '../itinerary/itineraryStore';
import { useRecipeStore } from '../recipe/recipeStore';
import { useStaticInfoStore } from '../staticInfo/staticInfoStore';
import { useTripStore } from '../trip/tripStore';

export const useProductDetailStore = defineStore('ProductDetailStore', () => {
  const itineraryStore = useItineraryStore();
  const staticInfoStore = useStaticInfoStore();
  const tripStore = useTripStore();
  const recipeStore = useRecipeStore();

  // State
  const destinationProductDetailCode = ref<string>();
  const overriddenNumberOfNights = ref<number | undefined>(undefined);
  const destinationProductDetailIngredientId = ref<IngredientId>();
  const productSearchType = ref<ProductSearchType | undefined>(undefined);

  const destinationProductDetailStaticInfo = ref<DetailsStaticInfo>();

  const cachedProductCode = ref<string | undefined>();
  const cachedIngredientId = ref<IngredientId | undefined>();
  const cachedStaticInfo = ref<StaticDetailedDestinationProduct | undefined>();

  const accommodationDetailAvailabilityLoadingCounter = ref<number>(0);
  const accommodationDetailAvailability = ref<
    Dictionary<HotelBookableAndLockableOption>
  >({});

  const activityDetailAvailability = ref<ActivityAvailabilityResponse>({
    hasFixedDates: false,
    ratesByDay: [],
  });

  const transportProductDetail = ref<
    AvailableTransportIngredientResponseWithStaticInfo | undefined
  >();

  const accommodationDetailAvailabilityLoading = computed<boolean>(
    () => accommodationDetailAvailabilityLoadingCounter.value > 0
  );

  const destinationStaticProductDetailForHotel = computed<
    StaticDetailedDestinationProduct | undefined
  >(() => {
    const productCode = destinationProductDetailCode.value;
    const ingredientId = destinationProductDetailIngredientId.value;

    if (
      !productCode ||
      !ingredientId ||
      !productSearchType.value ||
      productSearchType.value === ProductSearchType.Activity
    ) {
      return;
    }

    const ingredientResponse =
      itineraryStore.getHotelIngredientResponse(ingredientId) ||
      itineraryStore.getOwnStayIngredientResponse(ingredientId);

    const staticInfo =
      destinationProductDetailStaticInfo.value?.hotelCode === productCode
        ? destinationProductDetailStaticInfo.value
        : tripStore.getStaticInfo(productCode, ingredientId);

    if (!staticInfo || !ingredientResponse) {
      if (
        productCode === cachedProductCode.value &&
        ingredientId === cachedIngredientId.value &&
        cachedStaticInfo.value
      ) {
        return {
          ...cachedStaticInfo.value,
          isAvailabilityLoading: accommodationDetailAvailabilityLoading.value,
        };
      }
      return;
    }

    const isHearted =
      isAccommodation(staticInfo.typeOfHotel) &&
      tripStore.hotelIsHearted(productCode, ingredientId);

    const isInItinerary =
      isAccommodation(staticInfo.typeOfHotel) &&
      tripStore.isHotelInItinerary(ingredientId, productCode);
    const numberOfNights = ingredientResponse.numberOfNights;
    const result: StaticDetailedDestinationProduct = {
      ingredientId,
      staticInfo,
      productCode,
      isHearted,
      isInItinerary,
      numberOfNights,
      isAvailabilityLoading: accommodationDetailAvailabilityLoading.value,
    };

    cachedProductCode.value = productCode;
    cachedIngredientId.value = ingredientId;
    cachedStaticInfo.value = result;
    return result;
  });

  const destinationDynamicProductDetailForHotel = computed<
    DynamicDetailedDestinationProduct | undefined
  >(() => {
    if (
      !productSearchType.value ||
      productSearchType.value === ProductSearchType.Activity
    ) {
      return;
    }

    return {
      availability: Object.values(accommodationDetailAvailability.value),
    };
  });

  const destinationStaticProductDetailForActivity = computed<
    StaticDetailedActivityProduct | undefined
  >(() => {
    const productCode = destinationProductDetailCode.value;
    const ingredientId = destinationProductDetailIngredientId.value;

    if (
      !productCode ||
      !ingredientId ||
      !productSearchType.value ||
      productSearchType.value !== ProductSearchType.Activity
    ) {
      return;
    }

    const ingredientResponse =
      itineraryStore.getHotelIngredientResponse(ingredientId) ||
      itineraryStore.getOwnStayIngredientResponse(ingredientId);

    if (!ingredientResponse) {
      return;
    }

    const parentIngredientId = ingredientResponse.ingredientId;
    const staticInfo =
      destinationProductDetailStaticInfo.value?.hotelCode === productCode
        ? destinationProductDetailStaticInfo.value
        : tripStore.getStaticInfo(productCode, parentIngredientId);

    if (!staticInfo) {
      return;
    }

    const isHearted =
      isAccommodation(staticInfo.typeOfHotel) &&
      tripStore.hotelIsHearted(productCode, ingredientId);

    const isInItinerary =
      isAccommodation(staticInfo.typeOfHotel) &&
      tripStore.isHotelInItinerary(ingredientId, productCode);
    const numberOfNights = ingredientResponse.numberOfNights;

    return {
      ingredientId,
      parentIngredientId,
      staticInfo,
      productCode,
      isHearted,
      isInItinerary,
      numberOfNights,
      isAvailabilityLoading: accommodationDetailAvailabilityLoading.value,
    };
  });

  const destinationDynamicProductDetailForActivity = computed<
    { availability: DayTicketRate[]; hasFixedDates: boolean } | undefined
  >(() => {
    if (
      !productSearchType.value ||
      productSearchType.value !== ProductSearchType.Activity
    ) {
      return;
    }

    return {
      availability: activityDetailAvailability.value.ratesByDay,
      hasFixedDates: activityDetailAvailability.value.hasFixedDates,
    };
  });

  watch(destinationStaticProductDetailForActivity, () => {
    updateActivityTicketsWithExistingSelectedTickets();
  });

  function updateActivityTicketsWithExistingSelectedTickets(): void {
    recipeStore.updateActivityTickets(
      destinationStaticProductDetailForActivity.value
        ? recipeStore.getExistingSelectedTickets(
            destinationStaticProductDetailForActivity.value.productCode,
            destinationStaticProductDetailForActivity.value.parentIngredientId
          )
        : []
    );
  }

  function isDetailStaticInfo(
    staticInfo:
      OverviewDestinationStaticInfo | DestinationStaticInfo | DetailsStaticInfo
  ): staticInfo is DetailsStaticInfo {
    return 'distances' in staticInfo && 'properties' in staticInfo;
  }

  function selectDestinationProduct(
    hotelCode: string,
    ingredientId: IngredientId,
    type: ProductSearchType
  ): void {
    clearTransportProductDetails();
    if (
      destinationProductDetailCode.value === hotelCode &&
      destinationProductDetailIngredientId.value === ingredientId &&
      productSearchType.value === type
    ) {
      return;
    }

    destinationProductDetailCode.value = hotelCode;
    destinationProductDetailIngredientId.value = ingredientId;
    productSearchType.value = type;
    accommodationDetailAvailability.value = {};
    activityDetailAvailability.value = { hasFixedDates: false, ratesByDay: [] };
  }

  function clearDestinationProductDetailAvailability(): void {
    accommodationDetailAvailability.value = {};
    activityDetailAvailability.value = { hasFixedDates: false, ratesByDay: [] };
  }

  function clearDestinationProductDetailStaticInfo(): void {
    destinationProductDetailStaticInfo.value = undefined;
  }

  function clearTransportProductDetails(): void {
    transportProductDetail.value = undefined;
  }

  function clearDestinationProductDetails(): void {
    destinationProductDetailCode.value = undefined;
    destinationProductDetailIngredientId.value = undefined;
    overriddenNumberOfNights.value = undefined;
    productSearchType.value = undefined;
    accommodationDetailAvailability.value = {};
    activityDetailAvailability.value = { hasFixedDates: false, ratesByDay: [] };
  }

  function clearProductDetails(): Promise<void> {
    clearDestinationProductDetails();
    clearTransportProductDetails();
    return Promise.resolve();
  }

  function updateNumberOfNights(amount: number | undefined): void {
    overriddenNumberOfNights.value = amount;
  }

  function setDestinationProductDetailStaticInfo(
    detailsStaticInfo: DetailsStaticInfo
  ): void {
    destinationProductDetailStaticInfo.value = detailsStaticInfo;
    staticInfoStore.insertOverviewStaticInfo({
      destinationStaticInfo: {},
      subIngredientsStaticInfo: {},
      heartedHotelsStaticInfo: {
        [getHotelKey(
          detailsStaticInfo.hotelCode,
          detailsStaticInfo.ingredientId
        )]: detailsStaticInfo,
      },
    });
  }

  function incrementAccommodationDetailAvailabilityLoadingCounter(): void {
    accommodationDetailAvailabilityLoadingCounter.value += 1;
  }

  function decrementAccommodationDetailAvailabilityLoadingCounter(): void {
    accommodationDetailAvailabilityLoadingCounter.value -= 1;
  }

  function updateAccommodationDetailAvailability(
    uniqueKey: string,
    value: HotelBookableAndLockableOption
  ): void {
    accommodationDetailAvailability.value[uniqueKey] = value;
  }

  function setActivityDetailAvailability(
    value: ActivityAvailabilityResponse
  ): void {
    activityDetailAvailability.value = value;
  }

  return {
    // State
    overriddenNumberOfNights: readonlyRef(overriddenNumberOfNights),
    destinationProductDetailCode: readonlyRef(destinationProductDetailCode),
    destinationProductDetailIngredientId: readonlyRef(
      destinationProductDetailIngredientId
    ),
    productSearchType: readonlyRef(productSearchType),

    accommodationDetailAvailabilityLoading,
    destinationStaticProductDetailForHotel,
    destinationDynamicProductDetailForHotel,
    destinationStaticProductDetailForActivity,
    destinationDynamicProductDetailForActivity,

    // Actions and mutations
    updateAccommodationDetailAvailability,
    selectDestinationProduct,
    isDetailStaticInfo,
    clearProductDetails,
    clearDestinationProductDetailAvailability,
    clearDestinationProductDetailStaticInfo,
    setDestinationProductDetailStaticInfo,
    setActivityDetailAvailability,
    updateNumberOfNights,
    incrementAccommodationDetailAvailabilityLoadingCounter,
    decrementAccommodationDetailAvailabilityLoadingCounter,
    updateActivityTicketsWithExistingSelectedTickets,
  };
});
